import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  TextField as Input,
  Menu,
  MenuItem,
  MenuSection,
  Text as MenuText,
  MenuTrigger,
  createIcon,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  Ban,
  CheckCircle,
  Loader2,
  MoreHorizontal,
  Search,
  Shield,
  User,
  UserCog,
} from 'lucide-react'
import { z } from 'zod'
import { authClient } from '@client/auth-client'
import { APP_ROUTES } from '@shared/app-routes'
import { getAdminUsers } from '@rpc/admin.rpc'
import type { FormEvent } from 'react'
import { fullWidthStyles, statusBadgeStyles } from '@/styles/s2-macros'

const adminUsersSearchSchema = z.object({
  query: z.string().optional(),
})

const BanIcon = createIcon(Ban)
const CheckCircleIcon = createIcon(CheckCircle)
const ShieldIcon = createIcon(Shield)
const UserCogIcon = createIcon(UserCog)
const UserIcon = createIcon(User)

export const Route = createFileRoute('/admin/users/')({
  validateSearch: (search) => adminUsersSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    query: search.query,
  }),
  loader: async ({ deps }) => {
    return { usersData: await getAdminUsers({ data: { query: deps.query } }) }
  },
  component: UsersPage,
})

function UsersPage() {
  const { usersData } = Route.useLoaderData()
  const router = useRouter()
  const [search, setSearch] = useState('')
  const [isProcessing, setIsProcessing] = useState<string | null>(null)

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    router.navigate({
      to: '/admin/users',
      search: { query: search || undefined },
      replace: true,
    })
  }

  const handleBan = async (userId: string, isBanned: boolean) => {
    setIsProcessing(userId)
    try {
      if (isBanned) {
        await authClient.admin.unbanUser({ userId })
      } else {
        await authClient.admin.banUser({ userId, banReason: 'Admin action' })
      }
      router.invalidate()
    } catch (e) {
      alert('Action failed')
    } finally {
      setIsProcessing(null)
    }
  }

  const handleRoleChange = async (
    userId: string,
    newRole: 'admin' | 'user',
  ) => {
    setIsProcessing(userId)
    try {
      await authClient.admin.setRole({ userId, role: newRole })
      router.invalidate()
    } catch (e) {
      alert('Auth role update failed')
    } finally {
      setIsProcessing(null)
    }
  }

  const handleImpersonate = async (userId: string) => {
    setIsProcessing(userId)
    try {
      await authClient.admin.impersonateUser({ userId })
      // Redirect to dashboard as the impersonated user
      window.location.href = APP_ROUTES.dashboard.home
    } catch (e) {
      alert('Impersonation failed')
      setIsProcessing(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            User management
          </h2>
          <p className="text-neutral-500">
            Manage user accounts, auth roles, billing plans, and bans.
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="w-full sm:w-72">
            <Input
              aria-label="Search users by email"
              placeholder="Search email..."
              value={search}
              onChange={setSearch}
            />
          </div>
          <Button
            type="submit"
            size="S"
            variant="secondary"
            fillStyle="outline"
            aria-label="Search users"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <Card styles={fullWidthStyles}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/70">
              <tr>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  User
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Auth role
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Billing plan
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Status
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Joined
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {usersData.users.map((user: any) => (
                <tr
                  key={user.id}
                  className="transition-colors hover:bg-neutral-50/70 dark:hover:bg-neutral-900/40"
                >
                  <td className="px-6 py-4">
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: user.id }}
                      className="flex items-center gap-3 group"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600">
                        {user.image ? (
                          <img
                            src={user.image}
                            alt={`${user.name || user.email} avatar`}
                            className="h-8 w-8 rounded-full"
                          />
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium group-hover:text-blue-600 transition-colors">
                          {user.name}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {user.email}
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="neutral" fillStyle="outline">
                      {formatAuthRoleLabel(user.role)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="neutral" fillStyle="subtle">
                      {formatBillingPlanLabel(user.billingPlan)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    {user.banned ? (
                      <Badge
                        variant="negative"
                        fillStyle="bold"
                        styles={statusBadgeStyles}
                      >
                        <Ban className="h-3 w-3" /> Banned
                      </Badge>
                    ) : (
                      <Badge
                        variant="positive"
                        fillStyle="subtle"
                        styles={statusBadgeStyles}
                      >
                        <CheckCircle className="h-3 w-3" /> Active
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-neutral-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <MenuTrigger align="end">
                      <Button
                        variant="secondary"
                        fillStyle="outline"
                        size="S"
                        aria-label={`Open actions for ${user.name || user.email}`}
                        isDisabled={isProcessing === user.id}
                      >
                        {isProcessing === user.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MoreHorizontal className="h-4 w-4" />
                        )}
                      </Button>
                      <Menu aria-label="User actions" size="L">
                        <MenuSection aria-label="Impersonation">
                          <MenuItem onAction={() => handleImpersonate(user.id)}>
                            <UserCogIcon slot="icon" aria-hidden />
                            <MenuText slot="label">Impersonate</MenuText>
                          </MenuItem>
                        </MenuSection>
                        <MenuSection aria-label="Auth role">
                          <MenuItem
                            onAction={() => handleRoleChange(user.id, 'admin')}
                          >
                            <ShieldIcon slot="icon" aria-hidden />
                            <MenuText slot="label">Make admin</MenuText>
                          </MenuItem>
                          <MenuItem
                            onAction={() => handleRoleChange(user.id, 'user')}
                          >
                            <UserIcon slot="icon" aria-hidden />
                            <MenuText slot="label">Make user</MenuText>
                          </MenuItem>
                        </MenuSection>
                        <MenuSection aria-label="Moderation">
                          <MenuItem
                            onAction={() => handleBan(user.id, !!user.banned)}
                          >
                            {user.banned ? (
                              <>
                                <CheckCircleIcon slot="icon" aria-hidden />
                                <MenuText slot="label">Unban user</MenuText>
                              </>
                            ) : (
                              <>
                                <BanIcon slot="icon" aria-hidden />
                                <MenuText slot="label">Ban user</MenuText>
                              </>
                            )}
                          </MenuItem>
                        </MenuSection>
                      </Menu>
                    </MenuTrigger>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50/80 p-4 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/70">
          <span>Total users: {usersData.total}</span>
          <div className="flex gap-2">{/* Pagination could go here */}</div>
        </div>
      </Card>
    </div>
  )
}

function formatAuthRoleLabel(role?: string) {
  if (!role) return 'User'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function formatBillingPlanLabel(plan?: string | null) {
  if (!plan) return 'Free'
  return plan.replace('User', '').replace(/^./, (char) => char.toUpperCase())
}
